import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { authApi, AuthResponse } from "redux/services/auth";
import env from "env";

export type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  expiresIn: number | null;
  organisationData?: {
    organisationId: string;
    leiNumber: string;
    orgToken: string;
    organizationRole?: string; // PSP, ENTERPRISE, etc.
  };
  userRole?: string[]; // Array of user roles from listRoles
};

const INITIAL_STATE: AuthState = {
  accessToken: null,
  refreshToken: null,
  expiresIn: null,
  organisationData: undefined,
  userRole: undefined,
};

// Transparent token refresh - uses token storage instead of Redux state
// Helper function to build form data for token refresh
const buildTokenRefreshFormBody = (refreshToken: string): string => {
  return [
    { key: "refresh_token", value: refreshToken },
    { key: "grant_type", value: "refresh_token" },
    { key: "scope", value: "openid profile" },
  ]
    .map(
      (n) =>
        encodeURIComponent(n.key) + "=" + encodeURIComponent(n.value || "")
    )
    .join("&");
};

// Helper function to create organization data from membership
const createOrganizationData = (firstMembership: any) => {
  if (!firstMembership) return undefined;

  return {
    organisationId: firstMembership.organisationId || '',
    leiNumber: firstMembership.leiNumber || '', // May be missing from API response
    orgToken: firstMembership.token || '',
    organizationRole: firstMembership.organisationRole,
  };
};

// Helper function to validate complete membership data
const isCompleteMembershipData = (membership: any): boolean => {
  return !!(membership && membership.organisationId && membership.token);
  // Removed leiNumber requirement - API doesn't provide it consistently
};

// Helper function to fetch organization data
const fetchOrganizationData = async (accessToken: string): Promise<{organizationData: any, userRole: any}> => {
  const membershipUrl = `${env.api.baseUrl.membershipService}users/me`;
  console.log("👥 [REFRESH TOKEN] Fetching organization data from:", membershipUrl);

  const membershipResponse = await fetch(membershipUrl, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });

  console.log("👥 [REFRESH TOKEN] Membership response", {
    status: membershipResponse.status,
    ok: membershipResponse.ok
  });

  if (membershipResponse.ok) {
    const membershipData = await membershipResponse.json();
    const firstMembership = membershipData?.data?.memberships?.[0];

    if (isCompleteMembershipData(firstMembership)) {
      const orgData = createOrganizationData(firstMembership);
      console.log("✅ [REFRESH TOKEN] Organization data fetched successfully", orgData);
      return {
        organizationData: orgData,
        userRole: membershipData?.data?.listRoles
      };
    } else {
      console.log("⚠️ [REFRESH TOKEN] Incomplete membership data during refresh", {
        hasMembership: !!firstMembership,
        hasOrgId: !!firstMembership?.organisationId,
        hasLeiNumber: !!firstMembership?.leiNumber,
        hasToken: !!firstMembership?.token,
      });
      const partialOrgData = createOrganizationData(firstMembership);
      return {
        organizationData: partialOrgData,
        userRole: membershipData?.data?.listRoles
      };
    }
  } else {
    const membershipError = await membershipResponse.json();
    console.error("❌ [REFRESH TOKEN] Failed to fetch organization data", {
      status: membershipResponse.status,
      error: membershipError
    });
    return {
      organizationData: undefined,
      userRole: undefined
    };
  }
};

export const refreshTokenTransparent = createAsyncThunk<AuthResponse>(
  "auth/refreshTokenTransparent",
  async (_, { getState }: any) => {
    const refreshToken = getState().auth.refreshToken;

    console.log("🔄 [REFRESH TOKEN] Starting token refresh process", {
      hasRefreshToken: !!refreshToken,
      refreshTokenLength: refreshToken?.length,
      timestamp: new Date().toISOString()
    });

    const requestUrl = `${env.api.baseUrl.tokenBaseUrl}oauth2/token`;
    const clientAuth = `${env.app.authCodeClientId}:${env.app.authCodeClientSecret}`;
    const encodedAuth = btoa(clientAuth);

    console.log("🔄 [REFRESH TOKEN] Request details", {
      url: requestUrl,
      clientId: env.app.authCodeClientId,
      clientSecretLength: env.app.authCodeClientSecret?.length,
      authHeader: `Basic ${encodedAuth.substring(0, 20)}...`,
      refreshToken: `${refreshToken?.substring(0, 8)}...`
    });

    const formBody = buildTokenRefreshFormBody(refreshToken as string);
    console.log("🔄 [REFRESH TOKEN] Request body", formBody);

    try {
      const response = await fetch(requestUrl, {
        method: "POST",
        headers: {
          Authorization: `Basic ${encodedAuth}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody,
      });

      console.log("🔄 [REFRESH TOKEN] Response received", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: response.headers?.entries ? Object.fromEntries(response.headers.entries()) : 'N/A (test environment)'
      });

      const content = await response.json();

      if (response.ok) {
        console.log("✅ [REFRESH TOKEN] Success", {
          hasAccessToken: !!content.access_token,
          hasRefreshToken: !!content.refresh_token,
          expiresIn: content.expires_in,
          tokenType: content.token_type,
          scope: content.scope
        });

        console.log("🔧 [REFRESH TOKEN] Token refresh successful - now fetching organization data");

        try {
          const { organizationData, userRole } = await fetchOrganizationData(content.access_token);
          return {
            ...content,
            organizationData,
            userRole
          };
        } catch (membershipError) {
          console.error("💥 [REFRESH TOKEN] Network error fetching organization data", membershipError);
          return {
            ...content,
            organizationData: undefined,
            userRole: undefined
          };
        }
      } else {
        console.error("❌ [REFRESH TOKEN] Failed", {
          status: response.status,
          error: content
        });

        return {
          ...content,
          organizationData: undefined,
          userRole: undefined
        };
      }
    } catch (error) {
      console.error("💥 [REFRESH TOKEN] Network error", error);
      throw error;
    }
  }
);

// Keep original refreshToken for backward compatibility 
export const refreshToken = refreshTokenTransparent;

export const setTokens = createAction<AuthResponse>("auth/setTokens");

const authSlice = createSlice({
  name: "auth",
  initialState: INITIAL_STATE,
  reducers: {
    logout: (state) => {
      return INITIAL_STATE;
    },
    setTokens: (state, { payload }) => {
      // Update both Redux state and transparent storage
      state.accessToken = payload.access_token;
      state.refreshToken = payload.refresh_token;
      state.expiresIn = payload.expires_in;

    },
    setOrganisationData: (state, { payload }: { payload: {
      organisationId: string;
      leiNumber: string;
      orgToken: string;
      organizationRole?: string;
    } }) => {
      state.organisationData = payload;
    },
    setUserRole: (state, { payload }: { payload: string[] }) => {
      state.userRole = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshToken.fulfilled, (state, { payload }) => {
        console.log("🔄 [AUTH SLICE] Token refresh completed, updating Redux state", {
          hasAccessToken: !!payload.access_token,
          hasRefreshToken: !!payload.refresh_token,
          expiresIn: payload.expires_in,
          hasOrganizationData: !!(payload as any).organizationData
        });

        // Update Redux state for consistency with API calls
        state.accessToken = payload.access_token;
        state.refreshToken = payload.refresh_token;
        state.expiresIn = payload.expires_in;

        // Update organization data if fetched during token refresh
        const organizationData = (payload as any).organizationData;
        if (organizationData) {
          state.organisationData = organizationData;
          console.log("🏢 [AUTH SLICE] Organization data updated from token refresh", organizationData);
        }

        // Update user roles if fetched during token refresh
        const userRole = (payload as any).userRole;
        if (userRole) {
          state.userRole = userRole;
          console.log("👤 [AUTH SLICE] User roles updated from token refresh", userRole);
          console.log("💾 [AUTH SLICE] Token refresh - persisted auth state updated with:", {
            organisationData: organizationData,
            userRole: userRole,
            hasTokens: !!(state.accessToken && state.refreshToken)
          });
        }

        console.log("🔧 [AUTH SLICE] Redux state updated with new tokens and organization data");
      })
      .addCase(refreshToken.rejected, (state, action) => {
        console.error("❌ [AUTH SLICE] Token refresh rejected", action.error);
        // On refresh failure, clear tokens and return to initial state
        return INITIAL_STATE;
      })
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, { payload }) => {
          // Login updates Redux state only - single source of truth
          state.accessToken = payload.access_token;
          state.refreshToken = payload.refresh_token;
          state.expiresIn = payload.expires_in;
        }
      )
      .addMatcher(
        (action) => action.type === "membershipApi/executeQuery/fulfilled" && action.meta?.arg?.endpointName === "getProfile",
        (state, action: any) => {
          console.log("🔍 [AUTH SLICE] getProfile matcher triggered");
          // Extract organization data from membership profile response
          const response = action.payload;
          const firstMembership = response?.data?.memberships?.[0];

          if (isCompleteMembershipData(firstMembership)) {
            const orgData = createOrganizationData(firstMembership);

            console.log("📊 [AUTH SLICE] Storing organization data from membership profile", orgData);
            state.organisationData = orgData;

            // Store user roles from profile response
            const userRoles = response?.data?.listRoles;
            if (userRoles) {
              state.userRole = userRoles;
              console.log("👤 [AUTH SLICE] Storing user roles from membership profile", userRoles);
              console.log("💾 [AUTH SLICE] Complete persisted auth state will be:", {
                organisationData: orgData,
                userRole: userRoles,
                hasTokens: !!(state.accessToken && state.refreshToken)
              });
            }
          } else {
            console.log("⚠️ [AUTH SLICE] Incomplete membership data, skipping organization data extraction", {
              hasMembership: !!firstMembership,
              hasOrgId: !!firstMembership?.organisationId,
              hasLeiNumber: !!firstMembership?.leiNumber,
              hasToken: !!firstMembership?.token,
            });
          }
        }
      );
  },
});

export default authSlice.reducer;
export const { logout, setOrganisationData, setUserRole } = authSlice.actions;

export const selectAuth = (state: {auth: AuthState}) => state.auth;
